<template>
  <!-- Reusable Studio edit modal — the Community "Conjure" look + transition:
       an opaque, blurred scrim and a card that slides in from the top. The
       scrim's RIGHT edge is offset (``rightOffset``) so the Mystic AIda chat pane
       on the right stays visible; the card hugs the chat's left edge. -->
  <!-- Teleported to <body>: the Studio's panel arrives with a transform/filter
       animation, which makes position:fixed measure against the panel, not the
       viewport — the scrim's right offset then cut the card down to a 200px
       sliver hugging nothing. -->
  <Teleport to="body">
  <Transition name="se-modal">
    <div
      v-if="open"
      class="se-overlay"
      :class="{ 'se-hug': align === 'start' }"
      :style="{ right: rightOffset + 'px' }"
      @click.self="$emit('close')"
    >
      <div class="se-card" :style="maxWidth ? { maxWidth: maxWidth + 'px' } : null">
        <header class="se-head">
          <h3 class="se-title">{{ title }}</h3>
          <button class="se-close" aria-label="Close" @click="$emit('close')">×</button>
        </header>
        <div class="se-body"><slot /></div>
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<script setup>
defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '' },
  // px from the viewport right where the scrim ends — keeps the right chat pane
  // uncovered. 0 = full width.
  rightOffset: { type: Number, default: 0 },
  // px cap on the card width. 0 = use the default.
  maxWidth: { type: Number, default: 0 },
  // 'start' = hug the chat (card pinned to the chat's left edge); 'center' =
  // centered standalone modal (no chat open).
  align: { type: String, default: 'center' },
})
defineEmits(['close'])
</script>

<style scoped>
.se-overlay {
  position: fixed; top: 0; left: 0; bottom: 0;  /* right set inline (= chat's left edge) */
  z-index: 1400;
  display: flex; align-items: center; justify-content: center;
  padding: 24px 16px; overflow-y: auto;
  background: linear-gradient(180deg, rgba(8,3,18,0.62), rgba(8,3,18,0.86));
  backdrop-filter: blur(3px);
}
/* Hug the chat: card sits at the right, 10px off the scrim's right edge. */
.se-overlay.se-hug { justify-content: flex-end; padding-right: 10px; }
.se-card {
  position: relative; width: 100%; max-width: 560px; box-sizing: border-box;
  background: #14071f; border: 1px solid rgba(240,217,163,0.22); border-radius: 18px;
  color: #e9dcff; box-shadow: 0 26px 60px -16px rgba(0,0,0,0.85);
  max-height: 88vh; overflow-y: auto;
}
.se-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px 8px; }
.se-title { font-family: 'Cinzel', serif; font-size: 18px; color: #f5e8c0; margin: 0; }
.se-close {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 50%;
  background: rgba(102,51,153,0.5); border: none; color: #e9dcff;
  font-size: 20px; line-height: 1; cursor: pointer; transition: all 0.15s;
}
.se-close:hover { background: rgba(102,51,153,0.85); color: #fff7e6; transform: scale(1.06); }
.se-body { padding: 4px 18px 20px; }

/* Slide-in, copied from the Conjure modal: scrim fades, card slides from -30px. */
.se-modal-enter-active, .se-modal-leave-active { transition: opacity 0.4s ease; }
.se-modal-enter-from, .se-modal-leave-to { opacity: 0; }
.se-modal-enter-active .se-card, .se-modal-leave-active .se-card {
  transition: transform 0.5s cubic-bezier(0.2,0,0.2,1), opacity 0.4s ease;
}
.se-modal-enter-from .se-card, .se-modal-leave-to .se-card { transform: translateY(-30px); opacity: 0; }
</style>
